import type { AppConfig, ReconnectStrategy, ServerConfig, TurnProtocol } from './types';

// Default ICE servers - simple config like backup version
export const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.hitv.com:3478' },
  { urls: 'stun:rtc.03kan.com:3400' },
  { urls: 'stun:rtc2.03kan.com:3400' },
  { urls: 'stun:rtc3.03kan.com:3400' },
  // TURN server for relay fallback (UDP only)
  {
    urls: 'turn:rtc.03kan.com:3400?transport=udp',
    username: 'hudd',
    credential: 'huddhudd',
  },
  {
    urls: 'turn:rtc2.03kan.com:3400?transport=udp',
    username: 'hudd',
    credential: 'huddhudd',
  },
  {
    urls: 'turn:rtc3.03kan.com:3400?transport=udp',
    username: 'hudd',
    credential: 'huddhudd',
  },
];

// TURN-TCP servers for relay fallback through firewalls
export const TURN_TCP_SERVERS: RTCIceServer[] = [
  {
    urls: 'turn:rtc.03kan.com:3400?transport=tcp',
    username: 'hudd',
    credential: 'huddhudd',
  },
  {
    urls: 'turn:rtc2.03kan.com:3400?transport=tcp',
    username: 'hudd',
    credential: 'huddhudd',
  },
  {
    urls: 'turn:rtc3.03kan.com:3400?transport=tcp',
    username: 'hudd',
    credential: 'huddhudd',
  },
];

// Default application configuration
export const DEFAULT_CONFIG: AppConfig = {
  // Network
  signaling: 'websocket',
  iceServers: DEFAULT_ICE_SERVERS,
  icePolicy: 'auto',  // 自动选择P2P或TURN
  iceTransportPolicy: 'all',
  turnProtocol: 'auto' as TurnProtocol,

  // Video
  videoCodec: 'auto',  // 默认保持现有浏览器行为（不调 setCodecPreferences）

  // Input
  remoteMouseEnabled: true,
  remoteKeyboardEnabled: true,

  // UI
  theme: 'dark',
  showStatusBar: true,
  statusBarVisibility: 'on',  // 默认始终显示状态栏
  statusBarOrientation: 'vertical',  // 默认竖向状态栏
  lowLatencyMode: true,
  clipboardSync: true,

  // Audio
  systemAudioEnabled: false,  // 默认关闭，用户在网页开启后才发送音频
  micAudioEnabled: false,     // 默认关闭麦克风

  // Game
  gameMouseMode: false,        // 默认关闭游戏鼠标模式

  // 码率控制（实验性，host 持久化）— 默认开启 2000/1000
  // host 端 LoadHostConfig 文件不存在时也按这个默认值启用，UI 与 host 一致
  fixedBitrateEnabled: true,
  fixedBitrateNvencKbps: 2000,
  fixedBitrateSwKbps: 1000,

  // 内网 TURN 测试开关，默认关闭
  hostInternalTurnOnly: false,
};

// Reconnection strategy (only for disconnects after successful connection)
export const RECONNECT_STRATEGY: ReconnectStrategy = {
  maxAttempts: 5,     // 增加重连次数
  baseDelay: 1000,
  maxDelay: 10000,    // 增加最大延迟
  backoffMultiplier: 1.5,
  jitter: 0.2,
};

// 状态栏自动隐藏延迟（毫秒）- 连接成功后显示时长
export const STATUS_BAR_AUTO_HIDE_DELAY_MS = 60000;  // 1分钟

// Input throttle settings
export const INPUT_THROTTLE_MS = 50; // ~20Hz

// Stats polling interval - 1秒更新一次，FPS计算更稳定
export const STATS_POLL_INTERVAL_MS = 1000;

// UI timing
export const DOCK_HOVER_DELAY_MS = 500;
export const DOCK_HIDE_DELAY_MS = 1500;
export const TOAST_DURATION_MS = 3000;

// Mouse button mapping (browser -> Windows)
export const MOUSE_BUTTON_MAP: Record<number, number> = {
  0: 1, // Left
  1: 4, // Middle
  2: 2, // Right
};

// Mouse event types
export const MOUSE_EVENT = {
  MOVE: 0,
  DOWN: 1,
  UP: 2,
  WHEEL: 3,
} as const;

// Keyboard event types
export const KEY_EVENT = {
  DOWN: 0,
  UP: 1,
} as const;

// Connection state display info
export const CONNECTION_STATE_INFO: Record<string, { label: string; description: string }> = {
  disconnected: { label: '未连接', description: '点击连接开始远程会话' },
  connecting: { label: '正在连接', description: '正在初始化引擎...' },
  signaling: { label: '信令协商', description: '正在协商媒体能力...' },
  ice_gathering: { label: 'ICE 收集', description: '正在交换 ICE 候选地址...' },
  ice_connecting: { label: 'ICE 连接', description: '正在穿透 NAT 防火墙...' },
  connected: { label: '已连接', description: '安全 P2P 链路已建立' },
  reconnecting: { label: '重连中', description: '正在尝试恢复连接...' },
  failed: { label: '连接失败', description: '无法建立连接' },
};

// Cursor type to CSS mapping
export const CURSOR_TYPE_MAP: Record<number, string> = {
  0: 'default',
  1: 'text',
  2: 'wait',
  3: 'crosshair',
  4: 'pointer',
  5: 'nwse-resize',
  6: 'nesw-resize',
  7: 'ew-resize',
  8: 'ns-resize',
  9: 'move',
  10: 'not-allowed',
  11: 'progress',
  12: 'help',
};

// Capture mode display names
export const CAPTURE_MODE_NAMES: Record<string, string> = {
  dxgi: 'DXGI Desktop Duplication',
  gdi: 'GDI (Legacy)',
};

// Encoder display names
export const ENCODER_NAMES: Record<string, string> = {
  nvenc: 'NVIDIA NVENC',
  ffmpeg: 'FFmpeg/x264',
  mf: 'Media Foundation',
};

// Default server list
export const DEFAULT_SERVERS: ServerConfig[] = [
  {
    id: 'local',
    name: '本地服务器',
    host: 'localhost',
    port: 8080,
    wsUrl: 'ws://localhost:8080/ws',
  },
  {
    id: 'cqcq',
    name: 'CQCQ 远程服务器',
    host: 'cqcq.wbtun.03kan.com',
    port: 880,
    wsUrl: 'ws://cqcq.wbtun.03kan.com:880/ws',
  },
];

// Local storage keys
export const STORAGE_KEYS = {
  SERVERS: 'webrtcgo_servers',
  LAST_SERVER: 'webrtcgo_last_server',
} as const;
